import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Switch,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import FilterListIcon from "@mui/icons-material/FilterList";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import { SORT_OPTIONS, TIPO_FILTERS } from "../constants";

const { primary, secondary, gray, stone, text, green } = themeColorsDesingSystem;

function FilterSection({ title, subtitle, children }) {
  return (
    <Box
      padding={2}
      style={{
        backgroundColor: stone.light,
        borderRadius: 12,
        border: `1px solid ${stone.main}`,
        height: "100%",
      }}
    >
      <Typography variant="subtitle2" style={{ color: text.primary, fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" style={{ color: gray.dark, display: "block", marginBottom: 12 }}>
          {subtitle}
        </Typography>
      )}
      {!subtitle && <Box marginBottom={1.5} />}
      {children}
    </Box>
  );
}

function ToggleChip({ label, active, onClick }) {
  return (
    <Chip
      label={label}
      clickable
      onClick={onClick}
      style={{
        borderRadius: 20,
        margin: 4,
        backgroundColor: active ? primary.main : "#fff",
        color: active ? "#fff" : text.primary,
        fontWeight: active ? 600 : 500,
        border: active ? "none" : `1px solid ${stone.medium}`,
      }}
    />
  );
}

function countActiveFilters(filters) {
  let count = filters.tipos.length + filters.categorias.length + filters.campanhas.length;
  if (!filters.apenasAtivos) count += 1;
  return count;
}

export default function FiltersModal({
  open,
  onClose,
  filters,
  categoriasOptions,
  campanhasOptions,
  onToggleFilter,
  onSetFilters,
  onClearFilters,
}) {
  const activeCount = countActiveFilters(filters);

  const handleClear = () => {
    onClearFilters();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { borderRadius: 16, overflow: "hidden" } }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        padding="20px 24px"
        style={{
          background: `linear-gradient(135deg, ${primary.main} 0%, ${primary.dark} 100%)`,
        }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 14 }}>
          <Box
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TuneIcon style={{ color: "#fff" }} />
          </Box>
          <Box>
            <Typography variant="h4" style={{ color: "#fff", fontWeight: 700 }}>
              Filtros
            </Typography>
            <Typography variant="caption" style={{ color: "rgba(255,255,255,0.85)" }}>
              Refine os materiais exibidos na sua loja
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} aria-label="Fechar" style={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent style={{ padding: 24, backgroundColor: "#fff" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FilterSection title="Tipo de conteúdo" subtitle="Selecione um ou mais tipos">
              <Box display="flex" flexWrap="wrap" style={{ margin: -4 }}>
                {TIPO_FILTERS.map((tipo) => (
                  <ToggleChip
                    key={tipo.value}
                    label={tipo.label}
                    active={filters.tipos.includes(tipo.value)}
                    onClick={() => onToggleFilter("tipos", tipo.value)}
                  />
                ))}
              </Box>
            </FilterSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <FilterSection title="Ordenação" subtitle="Como os resultados serão exibidos">
              <Box display="flex" flexWrap="wrap" style={{ margin: -4 }}>
                {SORT_OPTIONS.map((opt) => (
                  <ToggleChip
                    key={opt.value}
                    label={opt.label}
                    active={filters.ordenacao === opt.value}
                    onClick={() => onSetFilters((prev) => ({ ...prev, ordenacao: opt.value }))}
                  />
                ))}
              </Box>
            </FilterSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <FilterSection title="Categoria" subtitle="Filtre por área de conteúdo">
              <Box
                display="flex"
                flexWrap="wrap"
                style={{ margin: -4, maxHeight: 140, overflowY: "auto" }}
              >
                {categoriasOptions.map((cat) => (
                  <ToggleChip
                    key={cat}
                    label={cat}
                    active={filters.categorias.includes(cat)}
                    onClick={() => onToggleFilter("categorias", cat)}
                  />
                ))}
              </Box>
            </FilterSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <FilterSection title="Campanha" subtitle="Vincule a uma campanha específica">
              <Box
                display="flex"
                flexWrap="wrap"
                style={{ margin: -4, maxHeight: 140, overflowY: "auto" }}
              >
                {campanhasOptions.map((camp) => (
                  <ToggleChip
                    key={camp}
                    label={camp}
                    active={filters.campanhas.includes(camp)}
                    onClick={() => onToggleFilter("campanhas", camp)}
                  />
                ))}
              </Box>
            </FilterSection>
          </Grid>

          <Grid item xs={12}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              padding="16px 20px"
              style={{
                borderRadius: 12,
                border: `1px solid ${filters.apenasAtivos ? green.main : stone.medium}`,
                backgroundColor: filters.apenasAtivos ? `${green.main}0D` : stone.light,
              }}
            >
              <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                <FilterListIcon style={{ color: filters.apenasAtivos ? green.main : gray.dark }} />
                <Box>
                  <Typography variant="subtitle2" style={{ color: text.primary, fontWeight: 600 }}>
                    Somente conteúdos vigentes
                  </Typography>
                  <Typography variant="caption" style={{ color: gray.dark }}>
                    Exibe apenas materiais dentro do período de validade
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={filters.apenasAtivos}
                onChange={(e) =>
                  onSetFilters((prev) => ({ ...prev, apenasAtivos: e.target.checked }))
                }
                color="primary"
              />
            </Box>
          </Grid>
        </Grid>

        {activeCount > 0 && (
          <Box
            marginTop={2}
            padding="10px 16px"
            style={{
              borderRadius: 8,
              backgroundColor: `${secondary.main}33`,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Typography variant="caption" style={{ color: primary.main, fontWeight: 600 }}>
              {activeCount} filtro{activeCount > 1 ? "s" : ""} ativo{activeCount > 1 ? "s" : ""}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        style={{
          padding: "16px 24px 24px",
          backgroundColor: stone.light,
          borderTop: `1px solid ${stone.main}`,
          gap: 12,
        }}
      >
        <Button variant="outlined" onClick={handleClear} style={{ borderRadius: 8, minWidth: 140 }}>
          Limpar filtros
        </Button>
        <Box flex={1} />
        <Button variant="outlined" onClick={onClose} style={{ borderRadius: 8, minWidth: 100 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onClose}
          style={{ borderRadius: 8, minWidth: 160, fontWeight: 600 }}
        >
          {activeCount > 0 ? `Aplicar (${activeCount})` : "Aplicar filtros"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
